import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { allProducts } from '../../mocks/products';

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SearchModal = ({ isOpen, onClose }: SearchModalProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    // Load recent searches from localStorage
    const saved = localStorage.getItem('recentSearches');
    if (saved) {
      setRecentSearches(JSON.parse(saved));
    }
  }, []);

  useEffect(() => {
    if (searchQuery.trim().length > 0) {
      setIsSearching(true);
      
      // Simulate search delay for better UX
      const timer = setTimeout(() => {
        const query = searchQuery.toLowerCase();
        const results = allProducts.filter(
          (product) =>
            product.name.toLowerCase().includes(query) ||
            product.description.toLowerCase().includes(query) ||
            product.category.toLowerCase().includes(query)
        );
        setSearchResults(results);
        setIsSearching(false);

        // Track search (will be sent to Supabase when connected)
        trackSearch(searchQuery, results.length);
      }, 300);

      return () => clearTimeout(timer);
    } else {
      setSearchResults([]);
      setIsSearching(false);
    }
  }, [searchQuery]);

  const trackSearch = async (query: string, resultsCount: number) => {
    // Save to recent searches
    const updated = [query, ...recentSearches.filter(s => s !== query)].slice(0, 5);
    setRecentSearches(updated);
    localStorage.setItem('recentSearches', JSON.stringify(updated));

    // TODO: Send to Supabase when connected
    console.log('Search tracked:', { query, resultsCount, timestamp: new Date() });
  };

  const handleProductClick = (product: any) => {
    // Track product click from search
    console.log('Product clicked from search:', product.name);
    
    // Navigate to products page with search filter
    navigate('/products', { state: { searchQuery: product.name, selectedProduct: product } });
    onClose();
  };

  const handleRecentSearchClick = (query: string) => {
    setSearchQuery(query);
  };

  const handleCategoryClick = (category: string) => {
    // Navigate to products page with category filter
    navigate('/products', { state: { category } });
    onClose();
  };

  const clearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem('recentSearches');
  };

  const handleViewAllResults = () => {
    if (searchQuery.trim()) {
      navigate('/products', { state: { searchQuery } });
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-20 px-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      ></div>

      {/* Search Modal */}
      <div className="relative w-full max-w-3xl bg-white rounded-2xl shadow-2xl overflow-hidden animate-slideDown">
        {/* Search Input */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center text-primary">
              <i className="ri-search-line text-2xl"></i>
            </div>
            <input
              ref={inputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && searchQuery.trim()) {
                  handleViewAllResults();
                }
              }}
              placeholder="Search products, categories, or keywords..."
              className="flex-1 text-lg outline-none text-gray-900 placeholder-gray-400"
            />
            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>
        </div>

        {/* Search Results */}
        <div className="max-h-[500px] overflow-y-auto">
          {searchQuery.trim().length === 0 ? (
            // Recent Searches & Popular Categories
            <div className="p-6">
              {recentSearches.length > 0 && (
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-900">Recent Searches</h3>
                    <button
                      onClick={clearRecentSearches}
                      className="text-sm text-primary hover:text-primary-dark cursor-pointer whitespace-nowrap"
                    >
                      Clear All
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {recentSearches.map((search, index) => (
                      <button
                        key={index}
                        onClick={() => handleRecentSearchClick(search)}
                        className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-time-line mr-2"></i>
                        {search}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Popular Categories</h3>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { name: 'Electronics', icon: 'ri-smartphone-line', color: 'bg-blue-50 text-blue-600' },
                    { name: 'Fashion', icon: 'ri-shirt-line', color: 'bg-pink-50 text-pink-600' },
                    { name: 'Health & Fitness', icon: 'ri-heart-pulse-line', color: 'bg-green-50 text-green-600' },
                    { name: 'Travel', icon: 'ri-plane-line', color: 'bg-purple-50 text-purple-600' },
                  ].map((category) => (
                    <button
                      key={category.name}
                      onClick={() => handleCategoryClick(category.name)}
                      className={`p-4 ${category.color} rounded-xl hover:shadow-md transition-all cursor-pointer text-left`}
                    >
                      <i className={`${category.icon} text-2xl mb-2`}></i>
                      <div className="font-medium">{category.name}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : isSearching ? (
            // Loading State
            <div className="p-12 text-center">
              <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-500">Searching...</p>
            </div>
          ) : searchResults.length > 0 ? (
            // Search Results
            <div className="p-4">
              <div className="flex items-center justify-between mb-4 px-2">
                <p className="text-sm text-gray-500">
                  Found {searchResults.length} {searchResults.length === 1 ? 'result' : 'results'}
                </p>
                {searchResults.length > 5 && (
                  <button
                    onClick={handleViewAllResults}
                    className="text-sm text-primary hover:text-primary-dark font-medium cursor-pointer whitespace-nowrap"
                  >
                    View All Results →
                  </button>
                )}
              </div>
              <div className="space-y-2">
                {searchResults.slice(0, 8).map((product) => (
                  <button
                    key={product.id}
                    onClick={() => handleProductClick(product)}
                    className="w-full flex items-center gap-4 p-3 hover:bg-gray-50 rounded-xl transition-colors cursor-pointer text-left"
                  >
                    <div className="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-900 truncate mb-1">
                        {product.name}
                      </h4>
                      <p className="text-sm text-gray-500 truncate">
                        {product.category}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-gray-900">${product.price}</div>
                      {product.commission && (
                        <div className="text-xs text-accent-green">
                          Earn ${product.commission}
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
              {searchResults.length > 8 && (
                <div className="mt-4 text-center">
                  <button
                    onClick={handleViewAllResults}
                    className="px-6 py-3 bg-gradient-to-r from-primary to-accent-green text-white rounded-lg font-medium hover:shadow-lg transition-all cursor-pointer whitespace-nowrap"
                  >
                    View All {searchResults.length} Results
                  </button>
                </div>
              )}
            </div>
          ) : (
            // No Results
            <div className="p-12 text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-4">
                <i className="ri-search-line text-4xl text-gray-400"></i>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">No results found</h3>
              <p className="text-gray-500 mb-6">
                Try searching with different keywords
              </p>
              <div className="flex flex-wrap gap-2 justify-center">
                <span className="text-sm text-gray-500">Try:</span>
                {['Laptop', 'Watch', 'Camera', 'Travel'].map((suggestion) => (
                  <button
                    key={suggestion}
                    onClick={() => setSearchQuery(suggestion)}
                    className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Quick Tips */}
        {searchQuery.trim().length === 0 && (
          <div className="p-4 bg-gray-50 border-t border-gray-200">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <i className="ri-lightbulb-line text-primary"></i>
              <span>
                <strong>Tip:</strong> Search by product name, category, or description. Press Enter to view all results.
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchModal;
